import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef } from 'react';
const MARK_CLASS = 'rounded-sm bg-accent/20 text-primary';
const PARAGRAPH_POINTER = /^\/paragraphs\/(\d+)$/;

// Where to highlight within the page's paragraphs: the pointed paragraph for a
// `/paragraphs/N` locator, else the first paragraph containing the cited passage (an
// `/excerpts/N` locator names a passage, not a position). -1 means "not locatable".
const findTargetIndex = (paragraphs: string[], pointer: string, cited: string): number => {
  const paragraphMatch = pointer.match(PARAGRAPH_POINTER);
  if (paragraphMatch) {
    const index = Number(paragraphMatch[1]);
    return index < paragraphs.length ? index : -1;
  }
  return paragraphs.findIndex(paragraph => paragraph.includes(cited));
};
const TargetParagraph = t0 => {
  const $ = _c(20);
  const {
    paragraph,
    cited,
    markRef
  } = t0;
  let start;
  let t1;
  let t2;
  if ($[0] !== cited || $[1] !== markRef || $[2] !== paragraph) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      start = paragraph.indexOf(cited);
      if (start < 0 || cited === paragraph) {
        let t3;
        if ($[6] !== markRef || $[7] !== paragraph) {
          t3 = <mark ref={markRef} className={MARK_CLASS}>{paragraph}</mark>;
          $[6] = markRef;
          $[7] = paragraph;
          $[8] = t3;
        } else {
          t3 = $[8];
        }
        t2 = t3;
        break bb0;
      }
      t1 = paragraph.slice(0, start);
    }
    $[0] = cited;
    $[1] = markRef;
    $[2] = paragraph;
    $[3] = start;
    $[4] = t1;
    $[5] = t2;
  } else {
    start = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[9] !== cited || $[10] !== markRef) {
    t3 = <mark ref={markRef} className={MARK_CLASS}>{cited}</mark>;
    $[9] = cited;
    $[10] = markRef;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== cited.length || $[13] !== paragraph || $[14] !== start) {
    t4 = paragraph.slice(start + cited.length);
    $[12] = cited.length;
    $[13] = paragraph;
    $[14] = start;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t1 || $[17] !== t3 || $[18] !== t4) {
    t5 = <>{t1}{t3}{t4}</>;
    $[16] = t1;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
};

/**
 * Renders a web page's fetched text (its stored paragraphs) with the cited passage highlighted and
 * scrolled into view — the text analog of HighlightedJson, showing the evidence in context rather
 * than as a detached snippet. When the page has no stored text, or the passage can't be located in
 * it, falls back to quoting the passage alone.
 */
const HighlightedText = t0 => {
  const $ = _c(12);
  const {
    paragraphs,
    pointer,
    cited
  } = t0;
  const containerRef = useRef(null);
  const markRef = useRef(null);
  const targetIndex = findTargetIndex(paragraphs, pointer, cited);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const container = containerRef.current;
      const mark = markRef.current;
      if (!container || !mark || !container.clientHeight) {
        return;
      }
      container.scrollTop = Math.max(0, mark.offsetTop - container.clientHeight / 3);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== cited || $[2] !== pointer) {
    t2 = [cited, pointer];
    $[1] = cited;
    $[2] = pointer;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useLayoutEffect(t1, t2);
  if (targetIndex < 0) {
    let t3;
    if ($[4] !== cited) {
      t3 = <blockquote className="max-h-72 overflow-auto whitespace-pre-wrap rounded-default border border-tertiary bg-secondary p-3 text-body-3 text-primary"><mark className={MARK_CLASS}>{cited}</mark></blockquote>;
      $[4] = cited;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t3;
  if ($[6] !== cited || $[7] !== paragraphs || $[8] !== targetIndex) {
    t3 = paragraphs.map((paragraph, index) => <p key={index} className="whitespace-pre-wrap text-body-3 text-primary">{index === targetIndex ? <TargetParagraph paragraph={paragraph} cited={cited} markRef={markRef} /> : paragraph}</p>);
    $[6] = cited;
    $[7] = paragraphs;
    $[8] = targetIndex;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <div ref={containerRef} className="relative flex max-h-72 flex-col gap-2 overflow-auto rounded-default border border-tertiary bg-secondary p-3">{t3}</div>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
export default HighlightedText;